import { useState } from "react";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import toast from "react-hot-toast";
import { api } from "../lib/api/client";

export type ProductType = "frame" | "badge" | "background";

export interface CreatePaymentIntentRequest {
  productType: ProductType;
  productId: string;
  platform: "web" | "mobile";
  currency?: string;
}

export interface CreatePaymentIntentResponse {
  ok: boolean;
  data?: {
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    currency: string;
    publishableKey?: string;
  };
  error?: string;
  message?: string;
}

export interface ConfirmPaymentResult {
  success: boolean;
  paymentIntentId?: string;
  error?: string;
}

export function useStripeCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Backend'e payment intent oluşturma isteği gönderir
   */
  const createPaymentIntent = async (
    productType: ProductType,
    productId: string,
    currency: string = "TRY"
  ): Promise<CreatePaymentIntentResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<CreatePaymentIntentResponse>(
        "/api/payments/stripe/create-intent",
        {
          productType,
          productId,
          platform: "web",
          currency,
        }
      );

      if (!response.ok || !response.data?.clientSecret) {
        const errMsg = response.error || response.message || "Payment intent oluşturulamadı";
        setError(errMsg);
        toast.error(errMsg);
        setLoading(false);
        return null;
      }

      setLoading(false);
      return response;
    } catch (err: any) {
      const errMsg = err.message || "Ödeme isteği oluşturulamadı";
      setError(errMsg);
      toast.error(errMsg);
      setLoading(false);
      return null;
    }
  };

  /**
   * Stripe confirmCardPayment ile ödemeyi tamamlar
   */
  const confirmCardPayment = async (
    stripe: Stripe | null,
    elements: StripeElements | null,
    clientSecret: string
  ): Promise<ConfirmPaymentResult> => {
    if (!stripe || !elements) {
      const errMsg = "Stripe yüklenemedi. Lütfen sayfayı yenileyin.";
      setError(errMsg);
      toast.error(errMsg);
      return { success: false, error: errMsg };
    }

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement("card") as any,
        },
      });

      if (stripeError) {
        const errMsg = stripeError.message || "Ödeme başarısız oldu";
        setError(errMsg);
        toast.error(errMsg);
        setLoading(false);
        return { success: false, error: errMsg };
      }

      if (paymentIntent?.status === "succeeded") {
        setLoading(false);
        return { success: true, paymentIntentId: paymentIntent.id };
      }

      const errMsg = "Ödeme tamamlanamadı";
      setError(errMsg);
      toast.error(errMsg);
      setLoading(false);
      return { success: false, error: errMsg };
    } catch (err: any) {
      const errMsg = err.message || "Ödeme sırasında hata oluştu";
      setError(errMsg);
      toast.error(errMsg);
      setLoading(false);
      return { success: false, error: errMsg };
    }
  };

  /**
   * Başarılı ödeme bildirimi
   */
  const showSuccess = (message: string = "Ödeme başarılı! 🎉") => {
    toast.success(message, { duration: 5000 });
  };

  /**
   * Hata bildirimi
   */
  const showError = (message: string) => {
    setError(message);
    toast.error(message);
  };

  return {
    loading,
    error,
    createPaymentIntent,
    confirmCardPayment,
    showSuccess,
    showError,
  };
}
